import { ViteReactSSG } from 'vite-react-ssg'
import { routes } from './App'
import { configureApi } from './lib/api'
import { initAnalytics } from './lib/analytics'
import { loadClerk } from './hooks/useClerk'
import './index.css'

// Routes that require an authenticated session. We only block token minting on
// clerk-js readiness here — on public marketing pages we must NOT eagerly pull
// in clerk-js, so any API call there keeps the old null-token-then-cookie path.
const onAuthedRoute = () =>
  typeof window !== 'undefined' &&
  /^\/(patient|select-type|login)\b/.test(window.location.pathname)

// Wait for clerk-js, but never block a request indefinitely on it: if clerk-js
// is slow or fails to load, fall back to the old null-token-then-cookie path
// rather than hanging getToken (which would stall every API call).
const CLERK_TOKEN_WAIT_MS = 5000
const awaitClerkReady = () =>
  Promise.race([
    loadClerk(),
    new Promise<void>((resolve) => setTimeout(resolve, CLERK_TOKEN_WAIT_MS)),
  ])

// vite-react-ssg owns rendering: it pre-renders the public marketing routes to
// static HTML at build time and hydrates them on the client. The router and its
// providers live in `routes` (see App.tsx). This module just exports the entry.
export const createRoot = ViteReactSSG({ routes }, ({ isClient }) => {
  // Client-only boot. None of this should run during static generation (no
  // window, no Clerk), so guard on isClient.
  if (!isClient) return

  initAnalytics()

  // Pre-warm clerk-js on authed routes so it's loading before the first patient
  // query fires (it's needed for the session anyway). Fire-and-forget; getToken
  // below awaits the same promise.
  if (onAuthedRoute()) void loadClerk()

  // Authenticate API calls with a freshly-minted Clerk session token rather than
  // relying solely on the background-rotated __session cookie. The cookie can
  // briefly hold an expired JWT (the window between expiry and clerk-js rotating
  // it), which surfaced as transient 401s — dashboard load stalls and the coach
  // bouncing users to /login. Clerk's getToken() returns a valid token, auto-
  // refreshing when near expiry.
  configureApi({
    getToken: async () => {
      try {
        // On authed routes, wait for clerk-js to finish loading before minting.
        // Without this the first dashboard/profile request races clerk-js, mints
        // no token, and 401s before the cookie/refresh path recovers it.
        if (onAuthedRoute()) await awaitClerkReady()
        return (await window.Clerk?.session?.getToken()) ?? null
      } catch {
        return null
      }
    },
    // On a 401, force a non-cached mint so an in-flight expiry is retried with a
    // genuinely fresh token rather than the one the server just rejected.
    refreshToken: async () => {
      try {
        if (onAuthedRoute()) await awaitClerkReady()
        return (await window.Clerk?.session?.getToken({ skipCache: true })) ?? null
      } catch {
        return null
      }
    },
  })
})
